import React from 'react';
import { mockData } from '../mockData';
import { Button } from './ui/button';
import { Check, ClipboardCheck, FileCheck, SearchCheck, ShieldCheck, Star } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import MobileSlider from './MobileSlider';

const PricingSection = ({ onOpenModal }) => {
  const handleGetTrademark = (plan) => {
    if (onOpenModal) {
      onOpenModal({
        name: plan.name,
        price: plan.price,
        tmSignUpType: plan.tmSignUpType
      });
    }
  };

  const attorneyPricingPlans = [
    { id: 'attorney-basic', name: 'Basic', price: 795, tmSignUpType: 2, notice: 'Dependable trademark assistance designed to simplify the filing journey', features: ['Attorney-Handled Trademark Filing from Start to Finish', 'Professional Case Evaluation', 'Application Preparation & Submission', 'Preliminary Trademark Search', 'State-Level E-Filing Included'] },
    { id: 'attorney-deluxe', name: 'Deluxe', price: 895, tmSignUpType: 2, notice: 'Personalized guidance through every stage of the registration process', features: ['Attorney-Led Trademark Filing', 'End-to-End Case Handling', 'Comprehensive Case Review & Analysis', 'Professional Application Preparation', 'Trademark Secured', 'USPTO-Compliant Filing Process'] },
    { id: 'attorney-premium', name: 'Premium', price: 1145, tmSignUpType: 2, notice: 'Structured support with detailed review and professional oversight', popular: true, features: ['Attorney-Led Case Review', 'Complete Case Filing & Management', 'Advanced Direct-Hit Trademark Search', 'Trademark Monitoring & Alerts', 'Comprehensive Risk & Refusal Meter', 'Attorney-Led Case Review'] },
    { id: 'attorney-elite', name: 'Elite', price: 1545, tmSignUpType: 2, filingFeeIncluded: true, notice: 'Responsive service with dedicated attention and efficient processing', features: ['Attorney-Led Case Review & Strategy', 'Complete Case Filing & Management', 'Advanced Direct-Hit Trademark Search', 'Federal & State Trademark Search Report', 'Trademark Monitoring & Real-Time Alerts', 'Attorney-Led Case Review & Strategy'] }
  ];

  const renderPricingCards = (plans) => (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {plans.map((plan) => (
        <div
          key={plan.id}
          className={`relative rounded-2xl p-8 transition-all duration-300 hover:scale-105 flex flex-col h-full ${
            plan.popular
              ? 'bg-gradient-to-br from-emerald-600 to-teal-500 text-white shadow-2xl ring-4 ring-emerald-200'
              : 'bg-white shadow-lg hover:shadow-xl border border-emerald-100'
          }`}
        >
          {plan.popular && (
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-yellow-400 text-gray-800 px-4 py-1 rounded-full text-sm font-bold flex items-center space-x-1">
                <Star size={16} fill="currentColor" />
                <span>POPULAR</span>
              </div>
            </div>
          )}

          <div className="text-center mb-6">
            <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-800'}`}>
              {plan.name}
            </h3>
            <div className="flex items-baseline justify-center">
              <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-brand-primary'}`}>
                ${plan.price}
              </span>
            </div>
            <p className={`text-sm mt-2 ${plan.popular ? 'text-emerald-50' : 'text-gray-500'}`}>
              {plan.filingFeeIncluded ? '*(Govt. filing fee included)' : '*(Govt. filing fee not included)'}
            </p>
            {plan.notice && (
              <div className={`mt-4 rounded-xl p-4 text-sm leading-relaxed ${plan.popular ? 'bg-white/15 text-white' : 'bg-emerald-50 text-gray-700'}`}>
                {plan.notice}
              </div>
            )}
          </div>

          <div className="flex-grow mb-6 overflow-y-auto max-h-64 pr-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-transparent">
            <div className="space-y-3">
              {plan.features.map((feature, idx) => (
                <div key={idx} className="flex items-start space-x-2">
                  <Check
                    className={`flex-shrink-0 mt-0.5 ${plan.popular ? 'text-emerald-100' : 'text-brand-primary'}`}
                    size={20}
                  />
                  <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-600'}`}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Button
            onClick={() => handleGetTrademark(plan)}
            className={`w-full mt-auto ${
              plan.popular
                ? 'bg-white text-brand-primary hover:bg-gray-50 border-2 border-brand-primary'
                : 'bg-brand-gradient text-white hover:opacity-90'
            }`}
          >
            Get a Trademark
          </Button>
        </div>
      ))}
    </div>
  );

  const renderProcessCards = (items) => {
    const processCard = (item) => {
      const Icon = item.icon;
      return (
        <div key={item.title} className="bg-white rounded-xl border border-gray-200 p-8 text-center shadow-sm hover:shadow-lg transition-all h-full">
          <div className="w-16 h-16 rounded-xl border-2 border-gray-900 flex items-center justify-center mx-auto mb-5">
            <Icon className="text-brand-primary" size={30} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
        </div>
      );
    };

    return (
      <div className="max-w-6xl mx-auto mb-12 myprocesscards">
        <div className="grid grid-cols-3 gap-6 max-[576px]:hidden">
          {items.map(processCard)}
        </div>
        <div className="hidden max-[576px]:block px-1 [&_.flex.justify-center.gap-2.mt-6]:mt-5 [&_.flex.justify-center.gap-2.mt-6]:items-center">
          <MobileSlider autoplay={true} showArrows={false} showDots={true}>
            {items.map(processCard)}
          </MobileSlider>
        </div>
      </div>
    );
  };

  return (
    <section id="pricing" className="py-20 bg-white via-teal-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
           Protecting Brands with  <span className="bg-brand-gradient bg-clip-text text-transparent">Trusted Trademark Support</span>
          </h2>
          <p className="text-base text-gray-600 max-w-5xl mx-auto leading-relaxed">
            Trademark Law Associates provides dependable trademark filing services designed to help businesses secure and strengthen their brand identity. Our team assists with every stage of the trademark process, from application preparation and filing to renewals and ongoing monitoring. Whether you are registering a company name, logo, phrase, or product identity, we offer tailored support based on your business objectives. We proudly assist clients across the United States and international markets with protecting valuable intellectual property as their brands continue to grow.
          </p>
        </div>

        <Tabs defaultValue="self-filing" className="w-full">
          <TabsList className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-transparent h-auto max-w-4xl mx-auto mb-14 p-0">
            <TabsTrigger value="self-filing" className="h-14 rounded-lg border border-brand-primary text-lg data-[state=active]:bg-brand-gradient data-[state=active]:text-white">
              Self Filing with Assistance
            </TabsTrigger>
            <TabsTrigger value="attorney-led" className="h-14 rounded-lg border border-brand-primary text-lg data-[state=active]:bg-brand-gradient data-[state=active]:text-white">
              IP Attorney-Led Filing
            </TabsTrigger>
          </TabsList>

          <TabsContent value="self-filing">
            {renderProcessCards([
              { icon: FileCheck, title: 'Application Research & Setup', desc: 'Trademark Law Associates reviews your trademark details, confirms the right classification, and prepares the required filing information for USPTO submission.' },
              { icon: SearchCheck, title: 'Verification & Approval Process', desc: 'After ID.me verification, you can review the prepared application and request any edits or updates before final approval.' },
              { icon: ShieldCheck, title: 'Official Trademark Filing', desc: 'Once confirmed, Trademark Law Associates submits your application to the USPTO with accurate and properly organized details.' }
            ])}
            <h3 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10">
              Self-Filing with Expert Assistance and <span className="text-brand-primary">Complete Support Plan for Easy Filing</span>
            </h3>
            {renderPricingCards(mockData.pricingPlans.map((plan) => ({ ...plan, tmSignUpType: 1 })))}
          </TabsContent>

          <TabsContent value="attorney-led">
            {renderProcessCards([
              { icon: ClipboardCheck, title: 'Trademark Detail Review', desc: 'We collect the essential trademark information, including the name, logo if applicable, and a brief description of your goods or services.' },
              { icon: FileCheck, title: 'Attorney Review & Filing Preparation', desc: 'Our IP attorney reviews your information, identifies the proper trademark class or classes, and prepares a structured USPTO application.' },
              { icon: ShieldCheck, title: 'Submission & Application Support', desc: 'We file the application on your behalf and handle the next steps, including application tracking and USPTO communication.' }
            ])}
            <h3 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10">
              Attorney Led Filing with <span className="text-brand-primary">Complete End-to-End Service Plan</span>
            </h3>
            {renderPricingCards(attorneyPricingPlans)}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default PricingSection;
